import { TurfStatus } from '@signalvote/shared';

/**
 * The single source of truth for how a turf's status looks, everywhere it is
 * shown: the map polygon fill/outline, the list row's dot and progress bar, and
 * the detail panel's dot and label.
 *
 * It exists because those three surfaces each grew their own palette, and they
 * drifted — the detail panel had been using Tailwind's named `yellow`/`blue`/
 * `green`, so the same turf rendered a visibly different colour depending on
 * which surface you looked at.
 *
 * Each status carries the colour twice, for the two consumers that cannot share
 * a representation:
 *
 * - `bg` is a Tailwind utility over the `--color-turf-*` tokens in index.css,
 *   for everything rendered as DOM. It is spelled out literally because
 *   Tailwind's JIT only emits CSS for class names it can find in the source, so
 *   a computed `bg-[${hex}]` would silently produce nothing.
 * - `hex` is the same colour as a literal, for the map. A Mapbox paint
 *   expression is evaluated off the DOM and cannot resolve a CSS variable.
 *
 * turfStatusDisplay.test.ts reads index.css and fails if the two disagree, so
 * changing a status colour means changing the token and the hex together.
 *
 * `text` stays a stock Tailwind class because the label is deliberately one step
 * darker than its dot for contrast.
 */
export const TURF_STATUS_DISPLAY: Record<
  TurfStatus,
  { bg: string; hex: string; text: string; label: string }
> = {
  [TurfStatus.Unassigned]: {
    bg: 'bg-turf-unassigned',
    hex: '#ef4444',
    text: 'text-red-600',
    label: 'Unassigned',
  },
  [TurfStatus.Assigned]: {
    bg: 'bg-turf-assigned',
    hex: '#f59e0b',
    text: 'text-amber-600',
    label: 'Assigned',
  },
  [TurfStatus.InProgress]: {
    bg: 'bg-turf-in-progress',
    hex: '#3478f6',
    text: 'text-blue-600',
    // The headline reads "In progress" (with a space) even though the enum is InProgress.
    label: 'In progress',
  },
  [TurfStatus.Completed]: {
    bg: 'bg-turf-completed',
    hex: '#22a45d',
    text: 'text-green-600',
    label: 'Completed',
  },
};

/**
 * Colour for a status the map doesn't recognise (Mapbox `match` needs a
 * fallback). Backed by `--color-turf-unknown`.
 */
export const TURF_STATUS_FALLBACK_HEX = '#9ca3af';

/**
 * The pace note shown beside a turf's progress. Only an in-progress turf has a
 * pace — everything else returns null and the caller shows nothing.
 */
export function getPaceLabel(
  progress: number,
  status: TurfStatus,
): { label: string; className: string } | null {
  if (status !== TurfStatus.InProgress) return null;
  if (progress < 25) return { label: 'Behind pace', className: 'text-amber-600' };
  if (progress < 55) return { label: 'On track', className: 'text-green-600' };
  return { label: 'Ahead', className: 'text-blue-600' };
}
